import * as React from "react";
import { QRCodeSVG } from "qrcode.react";
import "swiper/css";
import "swiper/css/pagination";
import { Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import Button from "../button";
import BaseModal from "../modal/base/BaseModal";
import { useTournamentQrCode } from "../../../@apis/hooks/useTournament";
import { getQueryClient } from "../../../@apis/queryClient";
import type { TournamentQrParams } from "../../../@apis/dto/request/tournament.request";

const QR_PLACEHOLDER_VALUE = "PARKGOLF-QR-PLACEHOLDER";

export type TournamentQrDialogItem = {
  tournamentId: number;
  params: TournamentQrParams;
  qrId: number;
  tournamentName: string;
  qrLabel: string;
  isCheckedIn: boolean;
};

/* 홈 QR 카드에서 여는 현장 인증 QR.
 * 인증 코드는 보관하지 않고 열 때마다 발급받는다.
 */
interface TournamentQrCodeDialogProps extends ModalComponentProps {
  items: TournamentQrDialogItem[];
  initialIndex?: number;
}

type QrSlideState = {
  code: string | null;
  hasError: boolean;
};

function DialogQrSlide({
  item,
  itemKey,
  onStateChange,
}: {
  item: TournamentQrDialogItem;
  itemKey: string;
  onStateChange: (key: string, state: QrSlideState) => void;
}) {
  const isDisabled = item.isCheckedIn;
  const [code, setCode] = React.useState<string | null>(null);
  const [hasError, setHasError] = React.useState(false);
  const { mutateAsync } = useTournamentQrCode(item.tournamentId, item.params);

  React.useEffect(() => {
    if (isDisabled) {
      onStateChange(itemKey, { code: null, hasError: false });
      return;
    }

    let alive = true;

    (async () => {
      try {
        const response = await mutateAsync(item.qrId);
        if (!alive) return;
        if (!response.success || !response.data?.qrCode) {
          setHasError(true);
          onStateChange(itemKey, { code: null, hasError: true });
          return;
        }
        setCode(response.data.qrCode);
        onStateChange(itemKey, {
          code: response.data.qrCode,
          hasError: false,
        });
      } catch {
        if (!alive) return;
        setHasError(true);
        onStateChange(itemKey, { code: null, hasError: true });
      }
    })();

    return () => {
      alive = false;
    };
  }, [isDisabled, mutateAsync, item.qrId, itemKey, onStateChange]);

  if (isDisabled) {
    return (
      <QRCodeSVG
        value={QR_PLACEHOLDER_VALUE}
        size={165}
        level="M"
        marginSize={0}
      />
    );
  }

  if (!code) {
    return (
      <p className="text-xs font-medium text-[#8e8e93] break-keep text-center whitespace-pre-line">
        {hasError
          ? "QR 코드를 불러오지 못했습니다.\n다시 시도해 주세요."
          : "QR 코드를 불러오는 중입니다."}
      </p>
    );
  }

  return <QRCodeSVG value={code} size={165} level="M" marginSize={0} />;
}

export default function TournamentQrCodeDialog({
  close,
  items,
  initialIndex = 0,
}: TournamentQrCodeDialogProps) {
  const startIndex = Math.min(Math.max(initialIndex, 0), Math.max(items.length - 1, 0));
  const [activeIndex, setActiveIndex] = React.useState(startIndex);
  const [slideStates, setSlideStates] = React.useState<Record<string, QrSlideState>>(
    {},
  );

  const handleSlideStateChange = React.useCallback(
    (key: string, state: QrSlideState) => {
      setSlideStates((prev) => ({ ...prev, [key]: state }));
    },
    [],
  );

  // 이 화면을 보는 동안 현장에서 인증이 찍힐 수 있으므로,
  // 닫는 경로(닫기 버튼 / 딤 클릭)와 무관하게 QR 목록을 다시 받아온다.
  const handleClose = () => {
    getQueryClient().invalidateQueries({ queryKey: ["auth-my-info"] });
    close();
  };

  // 대회명, 라벨, 하단 인증 코드도 현재 슬라이드에 맞춰 같이 바뀝니다.
  const activeItem = items[activeIndex] ?? items[0];
  const activeKey = activeItem
    ? `${activeItem.tournamentId}-${activeItem.qrId}`
    : "";
  const activeState = slideStates[activeKey];
  const activeCode = activeState?.code ?? null;

  return (
    <BaseModal className="p-[20px]" size="dialog" close={handleClose}>
      <section className="flex flex-col gap-[20px]">
        {activeItem && (
          <div className="flex flex-col items-center gap-[4px] text-center">
            <p className="text-base font-bold text-black break-keep">
              {activeItem.tournamentName}
            </p>
            <p className="text-3xl font-bold text-red-500 break-keep">
              {activeItem.isCheckedIn
                ? `${activeItem.qrLabel} 인증 완료`
                : activeItem.qrLabel}
            </p>
          </div>
        )}

        <div className="overflow-hidden rounded-[10px] bg-[#f2f2f7]">
          {/* TournamentQrCodeDialog의 회색 QR 박스 안에서 좌우로 넘기면 QR 이미지가 바뀝니다. */}
          <Swiper
            key={items.length}
            modules={[Pagination]}
            slidesPerView={1}
            initialSlide={startIndex}
            observer
            observeParents
            updateOnWindowResize
            pagination={
              items.length > 1
                ? { el: ".tournament-qr-pagination", clickable: true }
                : false
            }
            onSlideChange={(swiper) => setActiveIndex(swiper.activeIndex)}
          >
            {items.map((item) => {
              const key = `${item.tournamentId}-${item.qrId}`;
              return (
                <SwiperSlide key={key}>
                  <div className="relative overflow-hidden flex min-h-[195px] items-center justify-center p-[15px]">
                    {item.isCheckedIn && (
                      <div className="absolute inset-0 z-10 bg-black/40" />
                    )}
                    <DialogQrSlide
                      item={item}
                      itemKey={key}
                      onStateChange={handleSlideStateChange}
                    />
                  </div>
                </SwiperSlide>
              );
            })}
          </Swiper>
        </div>

        <Button label="닫기" variant="black" onClick={handleClose} />

        {items.length > 1 && (
          <div className="tournament-qr-pagination text-center w-full" />
        )}

        {activeCode && !activeItem?.isCheckedIn && (
          <div className="flex flex-col items-center gap-[8px]">
            <div className="flex items-center justify-center rounded-full bg-black px-[20px] py-[4px]">
              <p className="text-xl font-medium text-white break-all">
                {activeCode}
              </p>
            </div>
            <p className="text-center text-xs font-medium text-[#8e8e93] break-keep">
              QR 코드를 QR 리더기에 스캔해 주세요
            </p>
          </div>
        )}
      </section>
    </BaseModal>
  );
}
